import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CuentasPorCobrarService } from '../../../services/cuentas-por-cobrar.service';
import { ObrasService } from '../../../services/obras.service';
import { ProveedoresService } from '../../../services/proveedores.service';
import { HelpersService } from '../../../services/tools/helpers.service';

declare var moment;

/**
 * Vista "Estado de cuenta" del reporte de cuentas por cobrar.
 *
 * Recibe los filtros por queryParams, llama al mismo endpoint que el reporte
 * de tabla (cuentasPorCobrarService.getReporteCPC), agrupa los registros por
 * cliente (miembro / cliente_venta) y renderiza un HTML formal con encabezado,
 * detalle por cliente y totales. Cuando termina de renderizar dispara
 * window.print() para que el usuario lo guarde como PDF desde el navegador.
 *
 * No requiere libreria externa (no jsPDF, no dompdf). Aprovecha el dialogo
 * nativo de impresion de Chrome/Edge/Firefox que tiene "Guardar como PDF".
 */
@Component({
  selector: 'app-estado-cuenta-cxc',
  templateUrl: './estado-cuenta-cxc.component.html',
  styleUrls: ['./estado-cuenta-cxc.component.scss']
})
export class EstadoCuentaCxcComponent implements OnInit, AfterViewInit {

    public loading: boolean = true;
    public cpc: any[] = [];
    public grupos: any[] = [];

    public idObra: number = 0;
    public idProveedor: number = 0;
    public fechaTermino: string = '';
    public incluirCobradas: boolean = false;
    public incluirCanceladas: boolean = false;

    public obraNombre: string = '';
    public proveedorNombre: string = '';
    public fechaEmision: string = '';

    public totalCargos: number = 0;
    public totalAbonos: number = 0;
    public totalPorCobrar: number = 0;

    private autoPrintDisparado: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private cuentasPorCobrarService: CuentasPorCobrarService,
        private obrasService: ObrasService,
        private proveedoresService: ProveedoresService,
        private helperService: HelpersService) {
    }

    ngOnInit() {
        this.fechaEmision = moment().format('DD/MM/YYYY HH:mm');

        this.route.queryParams.subscribe((params: any) => {
            this.idObra = params.idObra ? parseInt(params.idObra) : 0;
            this.idProveedor = params.idProveedor ? parseInt(params.idProveedor) : 0;
            this.fechaTermino = params.fechaTermino || '';
            this.incluirCobradas = params.incluirCobradas === 'true' || params.incluirCobradas === '1';
            this.incluirCanceladas = params.incluirCanceladas === 'true' || params.incluirCanceladas === '1';

            this.cargarNombres();
            this.cargarReporte();
        });
    }

    ngAfterViewInit() {
        // Polling simple: cuando termina la carga, dispara window.print() una sola vez.
        const t = setInterval(() => {
            if (!this.loading && !this.autoPrintDisparado) {
                this.autoPrintDisparado = true;
                clearInterval(t);
                setTimeout(() => { window.print(); }, 600);
            }
        }, 300);
    }

    private cargarNombres() {
        // Nombre de obra
        if (this.idObra && this.idObra !== 0) {
            this.obrasService.getObratoSelect().subscribe(
                (response) => {
                    const list = response.json().content || [];
                    const o = list.find((x: any) => x.idObras == this.idObra);
                    this.obraNombre = o ? (o.nombreObra || o.clave || ('Obra ' + this.idObra)) : ('Obra ' + this.idObra);
                },
                () => { this.obraNombre = 'Obra ' + this.idObra; }
            );
        } else {
            this.obraNombre = 'Todas las obras';
        }

        // Nombre del proveedor (si esta filtrado)
        if (this.idProveedor && this.idProveedor !== 0) {
            this.proveedoresService.getProveedorestoSelect().subscribe(
                (response) => {
                    const content = response.json().content || {};
                    const list = content.proveedores || [];
                    const p = list.find((x: any) => x.idProveedores == this.idProveedor);
                    this.proveedorNombre = p ? (p.nombre || ('Proveedor ' + this.idProveedor)) : ('Proveedor ' + this.idProveedor);
                },
                () => { this.proveedorNombre = 'Proveedor ' + this.idProveedor; }
            );
        } else {
            this.proveedorNombre = 'Todos';
        }
    }

    private cargarReporte() {
        this.loading = true;
        this.cuentasPorCobrarService.getReporteCPC(
            null,
            this.idObra,
            this.idProveedor,
            this.fechaTermino,
            this.incluirCobradas,
            this.incluirCanceladas
        ).subscribe(
            (response) => {
                this.cpc = response.json().content || [];
                this.procesarDatos();
                this.loading = false;
            },
            (error) => {
                this.helperService.httpGlobalError(error);
                this.loading = false;
            }
        );
    }

    /**
     * Agrupa cpc por cliente (miembro/cliente) y calcula totales.
     */
    private procesarDatos() {
        const mapa: { [key: string]: any } = {};

        this.totalCargos = 0;
        this.totalAbonos = 0;
        this.totalPorCobrar = 0;

        for (const c of this.cpc) {
            const nombre = c.id_venta ? (c.cliente || 'Sin nombre') : (c.miembro || 'Sin nombre');
            const rfc = c.rfc || '';
            const clave = nombre + '||' + rfc;

            if (!mapa[clave]) {
                mapa[clave] = {
                    nombre: nombre,
                    rfc: rfc,
                    espacio_trabajo: c.espacio_trabajo || '',
                    miembro_email: c.miembro_email || c.cliente_email || '',
                    items: [],
                    total_cargos: 0,
                    total_abonos: 0,
                    total_por_cobrar: 0
                };
            }

            const cargo = parseFloat(c.importe) || 0; // cargo = importe total (bruto+IVA), sin descontar lo pagado
            const abono = parseFloat(c.importe_cobrado) || 0;
            const saldo = parseFloat(c.total_por_cobrar) || 0; // saldo pendiente = total_por_cobrar (que ya descuenta cobrado) = cargo - abono

            mapa[clave].items.push({
                fecha: c.fecha_para_cobrar,
                concepto: c.concepto,
                tipo_cuenta: c.tipo_cuenta,
                factura: c.factura_folio || '',
                cargo: cargo,
                abono: abono,
                saldo: saldo,
                estatus: c.estatus
            });

            mapa[clave].total_cargos += cargo;
            mapa[clave].total_abonos += abono;
            mapa[clave].total_por_cobrar += saldo;

            this.totalCargos += cargo;
            this.totalAbonos += abono;
            this.totalPorCobrar += saldo;
        }

        // Ordenar items de cada grupo por fecha asc
        Object.keys(mapa).forEach((k) => {
            mapa[k].items.sort((a: any, b: any) => {
                return new Date(a.fecha).getTime() - new Date(b.fecha).getTime();
            });
        });

        // Ordenar grupos por nombre
        this.grupos = Object.keys(mapa)
            .map(k => mapa[k])
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }

    public reimprimir() {
        window.print();
    }

    public volver() {
        // Si vienen de window.open en otra pestania, no hay history util, cierra.
        if (window.opener) {
            window.close();
        } else {
            this.router.navigate(['/catalogo-reportes/reporte-cuentas-por-cobrar']);
        }
    }

    public formatFecha(f: any): string {
        if (!f) return '';
        return moment(f).format('DD/MM/YYYY');
    }
}



// WEBPACK FOOTER //
// ./src/app/components/reporte-cxc/estado-cuenta-cxc/estado-cuenta-cxc.component.ts